import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { GradientCard } from './ui/gradient-card';

const benefits = [
  {
    badgeText: "Wydajność",
    badgeColor: "#F59E0B", // Amber
    title: "Produktywność 3-5x",
    description: "Powtarzalne taski wykonują się same. Twój zespół skupia się na tym co naprawdę wymaga człowieka.",
    gradient: "orange" as const,
    imageUrl: "/assets/icon_efficiency.webp",
    ctaText: "Dowiedz się więcej",
    ctaHref: "/uslugi"
  },
  {
    badgeText: "Doświadczenie",
    badgeColor: "#8B5CF6", // Purple
    title: "Lepsze doświadczenie klienta",
    description: "Voice agenty 24/7, RAG support w 5 sekund. Klient nie czeka na odpowiedź - dostaje ją od razu.",
    gradient: "purple" as const,
    imageUrl: "/assets/icon_experience.webp",
    ctaText: "Zobacz demo",
    ctaHref: "/blog/voice-agent-case-study-deweloperka"
  },
  {
    badgeText: "Niezawodność",
    badgeColor: "#4B5563", // Gray
    title: "Dostępność 24/7",
    description: "Sprzedaż i support nie kończą się o 17:00. Twoja firma działa kiedy konkurencja śpi.",
    gradient: "gray" as const,
    imageUrl: "/assets/icon_reliability.webp",
    ctaText: "Sprawdź szczegóły",
    ctaHref: "/uslugi#voice-agent"
  },
  {
    badgeText: "Oszczędność",
    badgeColor: "#10B981", // Green
    title: "Redukcja kosztów operacyjnych",
    description: "Mniej etatów na powtarzalną pracę. Średnio 25-40h/tydzień oszczędności na firmę.",
    gradient: "green" as const,
    imageUrl: "/assets/icon_savings.webp",
    ctaText: "Kalkulator ROI",
    ctaHref: "/kalkulator-roi"
  },
  {
    badgeText: "Analityka",
    badgeColor: "#3B82F6", // Blue
    title: "Wnioski z danych",
    description: "Każda interakcja jest logowana i analizowana. Wiesz dokładnie gdzie tracisz pieniądze i gdzie zarabiasz.",
    gradient: "gray" as const,
    imageUrl: "/assets/icon_analytics.webp",
    ctaText: "Zobacz raporty",
    ctaHref: "#raporty"
  },
  {
    badgeText: "Wzrost",
    badgeColor: "#EF4444", // Red
    title: "Skalowalność bez zatrudniania",
    description: "Rośniesz 2-5x bez liniowego wzrostu kosztów osobowych. To jest sens AI w biznesie.",
    gradient: "orange" as const,
    imageUrl: "/assets/icon_growth.webp",
    ctaText: "Rozpocznij wzrost",
    ctaHref: "/quiz"
  }
];

export default function SolutionsBenefits() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section aria-label="Benefits" className="py-16 md:py-24 bg-surface">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
        
        {/* Header */}
        <motion.div
          className="mb-16 md:mb-24"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-stroke"></div>
            <span className="text-xs text-muted uppercase tracking-[0.3em]">Co zyskasz</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-text-primary mb-4">
            Sześć powodów <span className="font-display italic">dla których to działa</span>
          </h2>
        </motion.div>

        {/* Grid */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {benefits.map((benefit, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.1 * idx, ease: "easeOut" }}
              className="h-full"
            >
              <GradientCard
                badgeText={benefit.badgeText}
                badgeColor={benefit.badgeColor}
                title={benefit.title}
                description={benefit.description}
                gradient={benefit.gradient}
                imageUrl={benefit.imageUrl}
                ctaText={benefit.ctaText}
                ctaHref={benefit.ctaHref}
                className="!bg-bg" // Override default background to match theme
              />
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
